import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import employeeService from '../../../services/employeeService';
import employeeDeploymentService from '../../../services/employeeDeploymentService';
import employeeCodeSettingsService from '../../../services/employeeCodeSettingsService';
import shiftsService from '../../../services/shiftsService';
import Select from '../../../components/ui/Select';
import OvertimeSettings from './OvertimeSettings';

const EmployeeModal = ({ isOpen, onClose, employee, onSave, mode = 'view' }) => {
  const [activeTab, setActiveTab] = useState('personal');
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    employee_code: '',
    employee_id: '',
    department: '',
    position: '',
    hire_date: '',
    status: 'onboarding',
    address: '',
    emergencyContact: '',
    bankAccount: '',
    ifscCode: '',
    panNumber: '',
    aadharNumber: '',
    work_start_time: '09:00',
    work_end_time: '18:00',
    branch: '',
    schedule: '',
    shift_id: '',
    face_photo: null,
    face_photo_preview: null,
    login_enabled: false,
    login_email: '',
    login_password: '',
    pf_calculation_type: 'attendance_based',
    fixed_pf_amount: ''
  });
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentDeployment, setCurrentDeployment] = useState(null);
  const [deploymentHistory, setDeploymentHistory] = useState([]);
  const [loadingDeployment, setLoadingDeployment] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [employeeIdPreview, setEmployeeIdPreview] = useState('');
  const [loadingEmployeeIdPreview, setLoadingEmployeeIdPreview] = useState(false);

  useEffect(() => {
    const fetchShifts = async () => {
      if (!isOpen) return;
      try {
        const result = await shiftsService.getShifts({ status: 'active' });
        if (result?.success) {
          setShifts(result.data || []);
        }
      } catch (error) {
        console.error('Failed to load shifts:', error);
      }
    };
    fetchShifts();
  }, [isOpen]);

  useEffect(() => {
    if (employee) {
      setFormData({
        full_name: employee.full_name || '',
        email: employee.email || '',
        phone: employee.phone || '',
        employee_code: employee.employee_code || '',
        employee_id: employee.employee_id || '',
        employeeId: employee.employee_id || employee.employeeId || '',
        department: employee.department || '',
        position: employee.position || '',
        designation: employee.position || employee.designation || '',
        hire_date: employee.hire_date || '',
        joiningDate: employee.hire_date || employee.joiningDate || '',
        status: employee.status || 'active',
        address: employee.address || '',
        emergencyContact: employee.emergency_contact || employee.emergencyContact || '',
        bankAccount: employee.bank_account || employee.bankAccount || '',
        ifscCode: employee.ifsc_code || employee.ifscCode || '',
        panNumber: employee.pan_number || employee.panNumber || '',
        aadharNumber: employee.aadhar_number || employee.aadharNumber || '',
        work_start_time: employee.work_start_time ? employee.work_start_time.substring(0, 5) : '09:00',
        work_end_time: employee.work_end_time ? employee.work_end_time.substring(0, 5) : '18:00',
        branch: employee.branch || '',
        schedule: employee.schedule || '',
        shift_id: employee.shift_id || '',
        face_photo: null,
        face_photo_preview: employee.face_photo_url || null,
        login_enabled: !!employee.user_id,
        login_email: employee.email || '',
        login_password: '',
        pf_calculation_type: employee.pf_calculation_type || 'attendance_based',
        fixed_pf_amount: employee.fixed_pf_amount || ''
      });

      // Load documents
      loadDocuments(employee.id);
      // Load deployment information
      loadDeploymentInfo(employee.id);
    } else {
      // Reset form for new employee
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        employee_code: '',
        employee_id: '',
        department: '',
        position: '',
        hire_date: '',
        status: 'onboarding',
        address: '',
        emergencyContact: '',
        bankAccount: '',
        ifscCode: '',
        panNumber: '',
        aadharNumber: '',
        work_start_time: '09:00',
        work_end_time: '18:00',
        branch: '',
        schedule: '',
        shift_id: '',
        face_photo: null,
        face_photo_preview: null,
        login_enabled: false,
        login_email: '',
        login_password: ''
      });
    }
  }, [employee]);

  useEffect(() => {
    const loadEmployeeIdPreview = async () => {
      if (mode !== 'add' || !isOpen || employee) {
        setEmployeeIdPreview('');
        return;
      }
      setLoadingEmployeeIdPreview(true);
      try {
        const result = await employeeCodeSettingsService.previewNextCode();
        if (result?.success) {
          setEmployeeIdPreview(result.data?.next_code || '');
        } else {
          setEmployeeIdPreview('');
        }
      } catch (error) {
        setEmployeeIdPreview('');
      } finally {
        setLoadingEmployeeIdPreview(false);
      }
    };

    loadEmployeeIdPreview();
  }, [employee, isOpen, mode]);

  // Cleanup object URLs on unmount or when preview changes
  useEffect(() => {
    return () => {
      if (formData.face_photo_preview && formData.face_photo_preview.startsWith('blob:')) {
        URL.revokeObjectURL(formData.face_photo_preview);
      }
    };
  }, [formData.face_photo_preview]);

  const loadDocuments = async (employeeId) => {
    if (!employeeId) return;
    setLoadingDocuments(true);
    try {
      const result = await employeeService.getEmployeeDocuments(employeeId);
      if (result.success) {
        setDocuments(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const loadDeploymentInfo = async (employeeId) => {
    if (!employeeId) return;
    setLoadingDeployment(true);
    try {
      const result = await employeeDeploymentService.getDeployments({
        employee_id: employeeId,
        status: 'all'
      });
      if (result.success) {
        const deployments = result.data || [];
        // Find current active deployment
        const active = deployments.find(d => d.status === 'active');
        setCurrentDeployment(active || null);
        // Set history (all deployments sorted by date)
        setDeploymentHistory(deployments.sort((a, b) =>
          new Date(b.deployment_start_date) - new Date(a.deployment_start_date)
        ));
      }
    } catch (error) {
      console.error('Failed to load deployment info:', error);
    } finally {
      setLoadingDeployment(false);
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !employee?.id) return;

    setUploading(true);
    try {
      const documentType = prompt('Enter document type (e.g., Resume, ID Proof, PAN Card, Bank Details):') || 'Other';
      const result = await employeeService.uploadEmployeeDocument(employee.id, file, documentType);

      if (result.success) {
        await loadDocuments(employee.id);
        alert('Document uploaded successfully');
      } else {
        alert('Failed to upload document: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Failed to upload document: ' + error.message);
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleDocumentDelete = async (documentId) => {
    if (!employee?.id) return;
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const result = await employeeService.deleteEmployeeDocument(employee.id, documentId);
      if (result.success) {
        await loadDocuments(employee.id);
        alert('Document deleted successfully');
      } else {
        alert('Failed to delete document: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Failed to delete document: ' + error.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Document types configuration
  const documentTypes = [
    { id: 'Aadhar', label: 'Aadhar Card', icon: 'IdCard', required: true },
    { id: 'PAN', label: 'PAN Card', icon: 'CreditCard', required: true },
    { id: 'Bank Details', label: 'Bank Details', icon: 'Building', required: false },
    { id: 'Resume', label: 'Resume', icon: 'FileText', required: false },
    { id: 'Salary Slip', label: 'Salary Slip', icon: 'DollarSign', required: false },
    { id: 'Experience Letter', label: 'Experience Letter', icon: 'Briefcase', required: false },
    { id: 'Offer Letter', label: 'Offer Letter', icon: 'FileCheck', required: false },
    { id: 'ID Proof', label: 'ID Proof', icon: 'UserCheck', required: false },
    { id: 'Address Proof', label: 'Address Proof', icon: 'MapPin', required: false },
    { id: 'Educational Certificate', label: 'Educational Certificate', icon: 'GraduationCap', required: false },
    { id: 'Other', label: 'Other Documents', icon: 'File', required: false }
  ];

  const getDocumentByType = (type) => {
    return documents.find(doc => doc.document_type === type);
  };

  const requiredDocumentTypes = documentTypes.filter(docType => docType.required);
  const requiredDocsUploaded = requiredDocumentTypes.filter(docType => getDocumentByType(docType.id));
  const missingRequiredDocs = requiredDocumentTypes.filter(docType => !getDocumentByType(docType.id));
  const hasAllRequiredDocs = requiredDocumentTypes.length === 0 || missingRequiredDocs.length === 0;

  const handleDocumentTypeUpload = async (documentType, file) => {
    if (!file || !employee?.id) return;

    setUploading(true);
    try {
      const result = await employeeService.uploadEmployeeDocument(employee.id, file, documentType);

      if (result.success) {
        await loadDocuments(employee.id);
        // Success is handled by UI update
      } else {
        alert('Failed to upload document: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Failed to upload document: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentTypeUpdate = async (documentId, newFile, documentType) => {
    if (!newFile || !employee?.id) return;

    // First delete the old document, then upload the new one
    try {
      await employeeService.deleteEmployeeDocument(employee.id, documentId);
      await handleDocumentTypeUpload(documentType, newFile);
    } catch (error) {
      alert('Failed to update document: ' + error.message);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      const image = new Image();
      image.onload = () => {
        // Create canvas for compression
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Calculate new dimensions (max 800x800 while maintaining aspect ratio)
        let width = image.width;
        let height = image.height;
        const maxDimension = 800;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(image, 0, 0, width, height);

        // Convert to blob with compression (quality 0.8, target size < 1MB)
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // If still too large, reduce quality further
              if (blob.size > 1024 * 1024) {
                canvas.toBlob(
                  (compressedBlob) => {
                    if (compressedBlob) {
                      const previewUrl = URL.createObjectURL(compressedBlob);
                      setFormData(prev => ({
                        ...prev,
                        face_photo: compressedBlob,
                        face_photo_preview: previewUrl
                      }));
                    }
                  },
                  'image/jpeg',
                  0.6
                );
              } else {
                const previewUrl = URL.createObjectURL(blob);
                setFormData(prev => ({
                  ...prev,
                  face_photo: blob,
                  face_photo_preview: previewUrl
                }));
              }
            }
          },
          'image/jpeg',
          0.8
        );
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    const positionValue = formData.position || formData.designation || '';
    const hireDateValue = formData.hire_date || formData.joiningDate || '';
    const missingFields = [];
    if (!formData.full_name?.trim()) missingFields.push('Full Name');
    if (!formData.email?.trim()) missingFields.push('Email Address');
    if (!formData.phone?.trim()) missingFields.push('Phone Number');
    if (!positionValue.trim()) missingFields.push('Designation');
    if (!hireDateValue.trim()) missingFields.push('Joining Date');
    if (formData.login_enabled && !employee?.user_id && !formData.login_password?.trim()) missingFields.push('Login Password');
    if (missingFields.length > 0) {
      window.alert(`Please fill required fields: ${missingFields.join(', ')}`);
      return;
    }
    if (formData.status === 'onboarding_complete' && !hasAllRequiredDocs) {
      window.alert('Please upload all required documents before completing onboarding.');
      return;
    }

    // Convert form data to API format (camelCase to snake_case where needed)
    const apiData = {
      ...formData,
      employee_id: formData.employee_id || formData.employeeId,
      position: formData.position || formData.designation,
      hire_date: formData.hire_date || formData.joiningDate,
      emergency_contact: formData.emergencyContact,
      bank_account: formData.bankAccount,
      ifsc_code: formData.ifscCode,
      pan_number: formData.panNumber,
      aadhar_number: formData.aadharNumber,
      // Remove camelCase duplicates
      employeeId: undefined,
      designation: undefined,
      joiningDate: undefined,
      emergencyContact: undefined,
      bankAccount: undefined,
      ifscCode: undefined,
      panNumber: undefined,
      aadharNumber: undefined
    };

    // Convert face_photo blob to base64 if it exists
    if (formData.face_photo instanceof Blob) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        apiData.face_photo = base64String;
        onSave(apiData);
        onClose();
      };
      reader.readAsDataURL(formData.face_photo);
    } else {
      onSave(apiData);
      onClose();
    }
  };

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: 'User' },
    { id: 'employment', label: 'Employment', icon: 'Briefcase' },
    { id: 'payroll', label: 'Payroll Details', icon: 'CreditCard' },
    ...(mode === 'add' ? [] : [{ id: 'attendance', label: 'Attendance', icon: 'Clock' }]),
    { id: 'documents', label: 'Documents', icon: 'FileText' },
    { id: 'login', label: 'Login Access', icon: 'Shield' }
  ];

  const attendanceData = [
    { date: '2025-01-20', checkIn: '09:15', checkOut: '18:30', status: 'Present', hours: '9h 15m' },
    { date: '2025-01-19', checkIn: '09:00', checkOut: '18:00', status: 'Present', hours: '9h 00m' },
    { date: '2025-01-18', checkIn: '-', checkOut: '-', status: 'Leave', hours: '0h 00m' },
    { date: '2025-01-17', checkIn: '09:30', checkOut: '18:45', status: 'Present', hours: '9h 15m' },
    { date: '2025-01-16', checkIn: '09:10', checkOut: '18:20', status: 'Present', hours: '9h 10m' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {mode === 'add' ? 'Add New Employee' : mode === 'edit' ? 'Edit Employee' : 'Employee Details'}
            </h2>
            {employee && (
              <p className="text-sm text-muted-foreground mt-1">
                {employee.name} • {employee.employeeId}
              </p>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${activeTab === tab.id
                  ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
              >
                <Icon name={tab.icon} size={16} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Personal Info Tab */}
          {activeTab === 'personal' && (
            <div className="space-y-6">
              {/* Face Photo Upload */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Face Photo (for attendance recognition)
                </label>
                <div className="flex items-center gap-4">
                  {formData.face_photo_preview ? (
                    <img
                      src={formData.face_photo_preview.startsWith('http') || formData.face_photo_preview.startsWith('blob:')
                        ? formData.face_photo_preview
                        : `${import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || `http://${window.location.hostname}:8000`}${formData.face_photo_preview}`}
                      alt="Face preview"
                      className="w-24 h-24 rounded-full object-cover border-2 border-border"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = 'flex';
                        }
                      }}
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center border-2 border-border">
                      <Icon name="User" size={32} className="text-muted-foreground" />
                    </div>
                  )}
                  {mode !== 'view' && (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        id="face-photo-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('face-photo-upload')?.click()}
                        className="flex items-center gap-2"
                      >
                        <Icon name="Upload" size={16} />
                        {formData.face_photo_preview ? 'Change Photo' : 'Upload Photo'}
                      </Button>
                      {formData.face_photo_preview && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            // Cleanup object URL if it's a blob URL
                            if (formData.face_photo_preview && formData.face_photo_preview.startsWith('blob:')) {
                              URL.revokeObjectURL(formData.face_photo_preview);
                            }
                            setFormData(prev => ({ ...prev, face_photo: null, face_photo_preview: null }));
                          }}
                          className="mt-2 text-destructive"
                        >
                          Remove
                        </Button>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        Photo will be automatically compressed to under 1MB
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Full Name"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  disabled={mode === 'view'}
                  required
                />
                <Input
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={mode === 'view'}
                  required
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  disabled={mode === 'view'}
                  required
                />
                <Input
                  label="Emergency Contact"
                  type="tel"
                  value={formData.emergencyContact}
                  onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
                  disabled={mode === 'view'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  disabled={mode === 'view'}
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                  placeholder="Enter complete address"
                />
              </div>
            </div>
          )}

          {/* Employment Tab */}
          {activeTab === 'employment' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Employee Code"
                  type="text"
                  value={formData.employee_code}
                  disabled={true}
                  placeholder={mode === 'add' ? 'Auto-generated on save' : ''}
                  className="bg-muted"
                  iconName="Hash"
                />
                <Input
                  label="Employee ID"
                  type="text"
                  value={mode === 'add' ? employeeIdPreview : (formData.employee_id || formData.employeeId || '')}
                  onChange={(e) => {
                    handleInputChange('employee_id', e.target.value);
                    handleInputChange('employeeId', e.target.value);
                  }}
                  disabled={mode === 'view' || mode === 'add'}
                  placeholder={loadingEmployeeIdPreview ? 'Loading...' : (mode === 'add' ? 'Auto-generated on save' : '')}
                  description={mode === 'add' ? 'Auto-generated based on Employee Code Configuration.' : ''}
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Department</label>
                  <select
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    disabled={mode === 'view'}
                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                  >
                    <option value="">Select Department</option>
                    <option value="Security Services">Security Services</option>
                    <option value="Housekeeping">Housekeeping</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="Administration">Administration</option>
                    <option value="Human Resources">Human Resources</option>
                  </select>
                </div>
                <Input
                  label="Designation"
                  type="text"
                  value={formData.position || formData.designation || ''}
                  onChange={(e) => {
                    handleInputChange('position', e.target.value);
                    handleInputChange('designation', e.target.value);
                  }}
                  disabled={mode === 'view'}
                  required
                />
                <Input
                  label="Joining Date"
                  type="date"
                  value={formData.hire_date || formData.joiningDate || ''}
                  onChange={(e) => {
                    handleInputChange('hire_date', e.target.value);
                    handleInputChange('joiningDate', e.target.value);
                  }}
                  disabled={mode === 'view'}
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    disabled={mode === 'view'}
                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                  >
                    <option value="onboarding">Onboarding</option>
                    <option value="onboarding_complete">Onboarding Complete</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                    <option value="probation">Probation</option>
                  </select>
                </div>
                <Input
                  label="Branch"
                  type="text"
                  value={formData.branch}
                  onChange={(e) => handleInputChange('branch', e.target.value)}
                  disabled={mode === 'view'}
                  placeholder="e.g., Main Office, Branch A"
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Schedule (Shift)</label>
                  <Select
                    value={formData.shift_id || ''}
                    onValueChange={(value) => {
                      const selectedShift = shifts.find(s => s.id.toString() === value.toString());
                      if (selectedShift) {
                        setFormData(prev => ({
                          ...prev,
                          shift_id: value,
                          schedule: selectedShift.name,
                          work_start_time: selectedShift.start_time.substring(0, 5),
                          work_end_time: selectedShift.end_time.substring(0, 5)
                        }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          shift_id: '',
                          schedule: '',
                          work_start_time: '09:00',
                          work_end_time: '18:00'
                        }));
                      }
                    }}
                    disabled={mode === 'view'}
                    options={[
                      { value: '', label: 'Select Shift' },
                      ...shifts.map(shift => ({
                        value: shift.id,
                        label: `${shift.name} (${shift.start_time.substring(0, 5)} - ${shift.end_time.substring(0, 5)})`
                      }))
                    ]}
                  />
                  {formData.work_start_time && formData.work_end_time && formData.shift_id && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Working hours: {formData.work_start_time} - {formData.work_end_time}
                    </p>
                  )}
                </div>
              </div>

              {/* PF Calculation Section */}
              <div className="mt-6 pt-6 border-t border-border">
                <h3 className="text-lg font-medium text-foreground mb-4">PF Calculation</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">PF Calculation Type</label>
                    <select
                      value={formData.pf_calculation_type || 'attendance_based'}
                      onChange={(e) => {
                        handleInputChange('pf_calculation_type', e.target.value);
                        // Reset fixed_pf_amount when type changes
                        if (e.target.value !== 'fixed') {
                          handleInputChange('fixed_pf_amount', '');
                        }
                      }}
                      disabled={mode === 'view'}
                      className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                    >
                      <option value="attendance_based">PF based on Attendance (12% of Basic Salary)</option>
                      <option value="attendance_based_max_amount">PF on Attendance but max amount ₹ 1800</option>
                      <option value="fixed">Fixed PF Amount</option>
                      <option value="none">No PF</option>
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formData.pf_calculation_type === 'attendance_based' 
                        ? 'PF will be calculated as 12% of Basic Salary based on attendance days'
                        : formData.pf_calculation_type === 'attendance_based_max_amount'
                        ? 'PF will be calculated as 12% of Basic Salary based on attendance days but max ₹ 1800'
                        : formData.pf_calculation_type === 'fixed'
                        ? 'A fixed amount will be deducted as PF every month'
                        : 'No PF will be deducted from salary'}
                    </p>
                  </div>
                  {(formData.pf_calculation_type === 'fixed' || !formData.pf_calculation_type) && (
                    <Input
                      label="Fixed PF Amount"
                      type="number"
                      value={formData.fixed_pf_amount || ''}
                      onChange={(e) => handleInputChange('fixed_pf_amount', e.target.value)}
                      disabled={mode === 'view'}
                      placeholder="Enter fixed PF amount"
                      iconName="IndianRupee"
                    />
                  )}
                </div>
              </div>

              {mode !== 'view' && (
                <div className="border border-border rounded-lg">
                  <OvertimeSettings
                    employeeId={employee?.id || null}
                    employeeName={employee?.full_name || formData.full_name || null}
                    allowGlobal={mode !== 'add'}
                  />
                </div>
              )}

              {/* Deployment Information */}
              {employee && (
                <div className="mt-6 pt-6 border-t border-border">
                  <h3 className="text-lg font-medium text-foreground mb-4">Deployment Information</h3>

                  {loadingDeployment ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Loading deployment info...</p>
                    </div>
                  ) : currentDeployment ? (
                    <div className="bg-muted/50 rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Icon name="MapPin" size={18} className="text-primary" />
                          <h4 className="font-semibold text-foreground">Current Deployment</h4>
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                          Active
                        </span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-24">Client Site:</span>
                          <span className="text-foreground font-medium">{currentDeployment.site_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-24">Client:</span>
                          <span className="text-foreground">{currentDeployment.company_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-24">Since:</span>
                          <span className="text-foreground">
                            {new Date(currentDeployment.deployment_start_date).toLocaleDateString('en-IN')}
                          </span>
                        </div>
                        {currentDeployment.deployment_end_date && (
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground w-24">Until:</span>
                            <span className="text-foreground">
                              {new Date(currentDeployment.deployment_end_date).toLocaleDateString('en-IN')}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.location.href = `/employee-deployment-management?employee_id=${employee.id}`}
                          className="text-xs"
                        >
                          <Icon name="ExternalLink" size={14} className="mr-2" />
                          View Deployment Details
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-muted/50 rounded-lg p-4 mb-4 text-center">
                      <Icon name="MapPin" size={24} className="mx-auto mb-2 text-muted-foreground opacity-50" />
                      <p className="text-sm text-muted-foreground">No active deployment</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.location.href = `/employee-deployment-management?employee_id=${employee.id}`}
                        className="mt-3 text-xs"
                      >
                        <Icon name="Plus" size={14} className="mr-2" />
                        Create Deployment
                      </Button>
                    </div>
                  )}

                  {/* Deployment History */}
                  {deploymentHistory.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-3">Deployment History</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {deploymentHistory.slice(0, 5).map((deployment) => (
                          <div key={deployment.id} className="flex items-center justify-between p-3 bg-card border border-border rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-foreground">{deployment.site_name}</span>
                                <span className={`text-xs px-2 py-0.5 rounded ${deployment.status === 'active' ? 'bg-green-100 text-green-800' :
                                  deployment.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                    deployment.status === 'transferred' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-gray-100 text-gray-800'
                                  }`}>
                                  {deployment.status}
                                </span>
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {new Date(deployment.deployment_start_date).toLocaleDateString('en-IN')}
                                {deployment.deployment_end_date &&
                                  ` - ${new Date(deployment.deployment_end_date).toLocaleDateString('en-IN')}`
                                }
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {deploymentHistory.length > 5 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.location.href = `/employee-deployment-management?employee_id=${employee.id}`}
                          className="mt-2 w-full text-xs"
                        >
                          View All ({deploymentHistory.length} deployments)
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Payroll Details Tab */}
          {activeTab === 'payroll' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Bank Account Number"
                  type="text"
                  value={formData.bankAccount}
                  onChange={(e) => handleInputChange('bankAccount', e.target.value)}
                  disabled={mode === 'view'}
                />
                <Input
                  label="IFSC Code"
                  type="text"
                  value={formData.ifscCode}
                  onChange={(e) => handleInputChange('ifscCode', e.target.value)}
                  disabled={mode === 'view'}
                />
                <Input
                  label="PAN Number"
                  type="text"
                  value={formData.panNumber}
                  onChange={(e) => handleInputChange('panNumber', e.target.value)}
                  disabled={mode === 'view'}
                />
                <Input
                  label="Aadhar Number"
                  type="text"
                  value={formData.aadharNumber}
                  onChange={(e) => handleInputChange('aadharNumber', e.target.value)}
                  disabled={mode === 'view'}
                />
              </div>
            </div>
          )}

          {/* Attendance Tab */}
          {activeTab === 'attendance' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Input
                  label="Work Start Time"
                  type="time"
                  value={formData.work_start_time || '09:00'}
                  onChange={(e) => handleInputChange('work_start_time', e.target.value)}
                  disabled={mode === 'view'}
                />
                <Input
                  label="Work End Time"
                  type="time"
                  value={formData.work_end_time || '18:00'}
                  onChange={(e) => handleInputChange('work_end_time', e.target.value)}
                  disabled={mode === 'view'}
                />
              </div>
              <div className="mb-6">
                <Select
                  label="Shift"
                  value={formData.shift_id || ''}
                  onValueChange={(value) => handleInputChange('shift_id', value)}
                  options={[
                    { value: '', label: 'No Shift Assigned' },
                    ...shifts.map(shift => ({
                      value: shift.id,
                      label: `${shift.name} (${shift.start_time.substring(0, 5)} - ${shift.end_time.substring(0, 5)})`
                    }))
                  ]}
                  disabled={mode === 'view'}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Assign a shift to use shift timings for attendance. If no shift is assigned, work start/end times will be used.
                </p>
              </div>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground">Recent Attendance</h3>
                <Button variant="outline" size="sm" iconName="Download">
                  Export Report
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border border-border rounded-lg">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Check In</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Check Out</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Hours</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {attendanceData.map((record, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {new Date(record.date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">{record.checkIn}</td>
                        <td className="px-4 py-3 text-sm text-foreground">{record.checkOut}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${record.status === 'Present' ? 'bg-green-100 text-green-800' :
                            record.status === 'Leave' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                            }`}>
                            {record.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">{record.hours}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-foreground">Employee Documents</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Manage employee documents by type. Required documents are marked with *
                  </p>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">Onboarding Checklist</h4>
                    <p className="text-xs text-muted-foreground">
                      Required documents: {requiredDocsUploaded.length}/{requiredDocumentTypes.length}
                    </p>
                  </div>
                  {mode !== 'view' && (
                    <Button
                      size="sm"
                      onClick={() => handleInputChange('status', 'onboarding_complete')}
                      disabled={!hasAllRequiredDocs}
                      title={hasAllRequiredDocs ? 'Mark onboarding as complete' : 'Upload all required documents first'}
                    >
                      Mark Onboarding Complete
                    </Button>
                  )}
                </div>
                {requiredDocumentTypes.length > 0 && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {requiredDocumentTypes.map(docType => {
                      const uploaded = !!getDocumentByType(docType.id);
                      return (
                        <div
                          key={docType.id}
                          className={`flex items-center gap-2 rounded-md px-3 py-2 text-xs ${uploaded ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'
                            }`}
                        >
                          <Icon name={uploaded ? 'CheckCircle' : 'AlertTriangle'} size={14} />
                          <span>{docType.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {loadingDocuments ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-sm text-muted-foreground">Loading documents...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {documentTypes.map((docType) => {
                    const existingDoc = getDocumentByType(docType.id);
                    const uploadId = `upload-${docType.id}`;
                    const updateId = `update-${docType.id}`;

                    return (
                      <div
                        key={docType.id}
                        className={`border rounded-lg p-4 ${existingDoc
                          ? 'border-green-200 bg-green-50/50'
                          : docType.required
                            ? 'border-amber-200 bg-amber-50/50'
                            : 'border-border bg-card'
                          }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${existingDoc ? 'bg-green-100' : 'bg-muted'
                              }`}>
                              <Icon
                                name={docType.icon || 'FileText'}
                                size={20}
                                className={existingDoc ? 'text-green-700' : 'text-muted-foreground'}
                              />
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-foreground flex items-center gap-1">
                                {docType.label}
                                {docType.required && <span className="text-destructive">*</span>}
                              </h4>
                              {existingDoc ? (
                                <p className="text-xs text-muted-foreground">
                                  {formatFileSize(existingDoc.file_size)} •
                                  Uploaded: {new Date(existingDoc.uploaded_at).toLocaleDateString('en-GB')}
                                </p>
                              ) : (
                                <p className="text-xs text-muted-foreground">
                                  {docType.required ? 'Required' : 'Optional'} • Not uploaded
                                </p>
                              )}
                            </div>
                          </div>
                          {existingDoc && (
                            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                              Uploaded
                            </span>
                          )}
                        </div>

                        {existingDoc ? (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-foreground truncate">
                              {existingDoc.document_name}
                            </p>
                            <div className="flex gap-2 flex-wrap">
                              <Button
                                variant="outline"
                                size="sm"
                                iconName="Eye"
                                onClick={() => {
                                  const baseURL = import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || `http://${window.location.hostname}:8000`;
                                  const url = `${baseURL}${existingDoc.download_url}?token=${localStorage.getItem('erp_auth_token')}`;
                                  // Open in new tab for viewing
                                  window.open(url, '_blank');
                                }}
                                className="flex-1"
                              >
                                View
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                iconName="Download"
                                onClick={() => {
                                  const baseURL = import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || `http://${window.location.hostname}:8000`;
                                  const url = `${baseURL}${existingDoc.download_url}?token=${localStorage.getItem('auth_token')}`;
                                  // Force download
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = existingDoc.document_name;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                                className="flex-1"
                              >
                                Download
                              </Button>
                              {mode !== 'view' && (
                                <>
                                  <input
                                    type="file"
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xls,.xlsx"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        handleDocumentTypeUpdate(existingDoc.id, file, docType.id);
                                      }
                                      e.target.value = '';
                                    }}
                                    className="hidden"
                                    id={updateId}
                                    disabled={uploading}
                                  />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    iconName="Edit"
                                    onClick={() => document.getElementById(updateId)?.click()}
                                    disabled={uploading}
                                    className="flex-1"
                                  >
                                    Replace
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    iconName="Trash2"
                                    onClick={() => handleDocumentDelete(existingDoc.id)}
                                    className="text-destructive hover:text-destructive"
                                    disabled={uploading}
                                  >
                                    Delete
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        ) : (
                          mode !== 'view' && employee?.id && (
                            <div>
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xls,.xlsx"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleDocumentTypeUpload(docType.id, file);
                                  }
                                  e.target.value = '';
                                }}
                                className="hidden"
                                id={uploadId}
                                disabled={uploading}
                              />
                              <Button
                                variant={docType.required ? "default" : "outline"}
                                size="sm"
                                iconName="Upload"
                                onClick={() => document.getElementById(uploadId)?.click()}
                                disabled={uploading}
                                className="w-full"
                              >
                                {uploading ? 'Uploading...' : `Upload ${docType.label}`}
                              </Button>
                            </div>
                          )
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {activeTab === 'login' && (
            <div className="space-y-6">
              <div className="bg-primary/5 p-4 rounded-lg flex items-start gap-4 mb-6 border border-primary/10">
                <div className="bg-primary/10 p-2 rounded-full">
                  <Icon name="Shield" className="text-primary" size={20} />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-primary">Login Access Control</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Manage the employee's access to the Self Service Portal. Email will be used as the username.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-lg border border-border">
                  <input
                    type="checkbox"
                    id="login_enabled"
                    checked={formData.login_enabled}
                    onChange={(e) => handleInputChange('login_enabled', e.target.checked)}
                    disabled={mode === 'view'}
                    className="w-5 h-5 text-primary border-border rounded focus:ring-primary cursor-pointer"
                  />
                  <div className="flex-1">
                    <label htmlFor="login_enabled" className="text-sm font-semibold text-foreground cursor-pointer block">
                      Enable Login Access
                    </label>
                    <p className="text-xs text-muted-foreground italic">
                      Allows this employee to log in using their email address.
                    </p>
                  </div>
                </div>

                {formData.login_enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-card border border-border rounded-lg shadow-sm animate-in fade-in slide-in-from-top-2">
                    <Input
                      label="Username (Login Email)"
                      type="email"
                      value={formData.login_email || formData.email}
                      onChange={(e) => handleInputChange('login_email', e.target.value)}
                      disabled={true}
                      iconName="Mail"
                      placeholder="employee@example.com"
                      description="Username is automatically linked to the employee's email address."
                    />
                    <Input
                      label={employee?.user_id ? "Update Password" : "Create Password"}
                      type="password"
                      value={formData.login_password || ''}
                      onChange={(e) => handleInputChange('login_password', e.target.value)}
                      disabled={mode === 'view'}
                      iconName="Lock"
                      placeholder="••••••••"
                      description={employee?.user_id
                        ? "Leave blank to keep current password."
                        : "Enter a password for the initial setup."
                      }
                      required={!employee?.user_id}
                    />
                  </div>
                )}
              </div>

              {!formData.login_enabled && employee?.user_id && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/30 rounded-lg">
                  <div className="flex gap-3">
                    <Icon name="AlertTriangle" className="text-amber-600 dark:text-amber-400 shrink-0" size={20} />
                    <div>
                      <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300">Warning: Disabling Access</h4>
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                        Unchecking this will prevent the employee from accessing the portal, but it won't delete their profile.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        {mode !== 'view' && (
          <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {mode === 'add' ? 'Add Employee' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeModal;
